import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Avatar, 
  Chip,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { 
  Book as BookIcon, 
  ShoppingCart as ShoppingCartIcon, 
  Add as AddIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, logout } from '../../Api/index.ts';

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [stats, setStats] = useState({
    totalListings: 0,
    activeListings: 0,
    totalSales: 0,
    savedBooks: 0
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await getCurrentUser();
        setUser(response.user);
        setStats({
          totalListings: 5,
          activeListings: 3,
          totalSales: 2,
          savedBooks: 8
        });
      } catch (error) {
        console.error('Error fetching user:', error);
        // Redirect to login if not authenticated
        navigate('/login');
        return;
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [navigate]);

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleCreateListing = () => {
    navigate('/user-listings');
  };

  const handleBrowseBooks = () => {
    navigate('/listings');
  };

  const handleViewProfile = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Logout */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" sx={{ 
            fontWeight: 'bold', 
            color: '#2c3e50',
            mb: 1
          }}>
            Welcome back, {user?.first_name}! 👋
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#7f8c8d',
            mb: 3
          }}>
            Ready to buy, sell, or discover textbooks?
          </Typography>
        </Box>
        
        <IconButton
          onClick={handleLogout}
          disabled={logoutLoading}
          sx={{
            color: '#e74c3c',
            '&:hover': {
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
            },
          }}
        >
          {logoutLoading ? <CircularProgress size={24} /> : <LogoutIcon />}
        </IconButton>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BookIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Active Listings</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.activeListings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingCartIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Total Sales</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.totalSales}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #F39C12 0%, #E67E22 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Total Listings</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.totalListings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BookIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Saved Books</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.savedBooks}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
            }
          }} onClick={handleCreateListing}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <AddIcon sx={{ fontSize: 60, color: '#6B89FF', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                Create New Listing
              </Typography>
              <Typography variant="body1" sx={{ color: '#7f8c8d', mb: 3 }}>
                Sell your textbooks and help other students save money
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5A75E6 0%, #4A65D6 100%)'
                  }
                }}
              >
                Start Selling
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
            }
          }} onClick={handleBrowseBooks}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <SearchIcon sx={{ fontSize: 60, color: '#2ECC71', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                Browse Textbooks
              </Typography>
              <Typography variant="body1" sx={{ color: '#7f8c8d', mb: 3 }}>
                Find affordable textbooks from students at your university
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #27AE60 0%, #229954 100%)'
                  }
                }}
              >
                Start Shopping
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Info Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ 
              width: 80, 
              height: 80, 
              mr: 3,
              background: 'linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%)',
              fontSize: '2rem'
            }}>
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="body1" sx={{ color: '#7f8c8d', mb: 1 }}>
                {user?.email}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SchoolIcon sx={{ fontSize: 16, color: '#6B89FF' }} />
                <Typography variant="body2" sx={{ color: '#6B89FF' }}>
                  {user?.school}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ ml: 'auto' }}>
              <Button 
                variant="outlined" 
                onClick={handleViewProfile}
                sx={{ 
                  borderColor: '#6B89FF', 
                  color: '#6B89FF',
                  '&:hover': {
                    borderColor: '#5A75E6',
                    backgroundColor: 'rgba(107, 137, 255, 0.1)'
                  }
                }}
              >
                View Profile
              </Button>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>You're logged in!</strong> Your session will remain active until you logout.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
            Recent Activity
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip 
              label="Logged In" 
              color="success" 
              size="small" 
              sx={{ mr: 2 }}
            />
            <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
              Welcome back! You're successfully logged in to bookloop.
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ color: '#7f8c8d', fontStyle: 'italic' }}>
            No recent activity yet. Start by creating your first listing or browsing textbooks!
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
} 